import React from 'react';
import { FormGroup, Spinner, Button } from 'reactstrap';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave, faTimes, faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
    FormValidated, Input, NumericInput, SmallButton,
} from '../common/forms/FormElements';
import CommonContext from '../Common';
import ValidationMessageDisplay from '../common/forms/ValidationMessageDisplay';
import { SaveButton } from '../common/forms/SaveButton';

const getRateStatusMessage = (payRate) => {
    const currentDate = new Date();
    const startDate = payRate.startDate ? new Date(payRate.startDate) : null;
    const endDate = payRate.endDate ? new Date(payRate.endDate) : null;

    if (startDate && currentDate >= startDate && (!endDate || currentDate <= endDate)) {
        return (
            <>
                <span className="pr-2">This is the current rate</span>
                <span className="fa fa-check-circle mr-2 text-success" />
            </>
        );
    } else if (startDate && currentDate < startDate) {
        return 'This rate is not active yet';
    } else if (endDate && currentDate > endDate) {
        return 'This rate has expired';
    }
    return null;
};

export class EmployeePayRate {
    id = null;

    payRate = null;

    startDate = null;

    endDate = null;

    rateReceivedDate = null;
}

export class EmployeePayRateForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            errors: {},
            payRate: this.props.payRate ?? new EmployeePayRate(),
        };
        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }
    onClose(response) {
        this.resetForm(); // Reset the form
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    resetForm = () => {
        this.setState({
            formValidated: false,
            payRate: new EmployeePayRate(), // Clear payRate state
            errors: {},
            validationMessage: null,
        });
    };

    onSubmit = async () => {
        const { onSaveCallback } = this.props;
        await onSaveCallback();
    };

    populateState = () => this.setState({ loading: false });

    render() {
        const {
            show, payRate, isValidated, setIsValidated, onChange,
        } = this.props;
        const { loading, errors, validationMessage } = this.state;

        if (loading) {
            return (
                <div className="d-flex flex-row h-100 align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }

        if (!payRate) {
            return '';
        }

        const classNames = cls(
            'slide-form col-xl-3 col-md-6 col-sm-12 pay-rate-form',
            { show },
        );

        return (
            <div className={classNames}>
                <FormValidated
                    ref={this.formRef}
                    setIsValidated={setIsValidated}
                    isValidated={isValidated}
                    className="m-0 pb-2 w-100"
                    id="payRateForm"
                    name="payRateForm"
                    onSubmit={this.onSubmit}
                >
                    <header className="slide-form-header border-bottom d-flex flex-column position-relative mb-0">
                        <Button
                            color="link"
                            id="payRateFormClose"
                            className="site-form-close"
                            onClick={this.onClose}
                        >
                            <FontAwesomeIcon
                                size="lg"
                                icon={faTimes}
                            />
                        </Button>

                        <div className="h5">
                            <div className="d-flex flex-row align-items-center mb-0 slide-form-title mb-0 pt-3 pl-3 pr-3">
                                <FontAwesomeIcon
                                    className="slide-form-icon mr-2 text-muted"
                                    size="lg"
                                    icon={faMoneyBillWave}
                                />
                                <span>
                                    {payRate.id
                                        ? 'Edit Pay Rate'
                                        : 'New Pay Rate'}
                                </span>
                            </div>
                        </div>

                        <div className="d-flex flex-row align-items-center mt-1 mb-2">
                            <SaveButton
                                type="submit"
                                name="payRateForm"
                                color="success"
                                size="sm"
                            />

                            {this.state.payRate.id ? (
                                <SmallButton onClick={this.onDelete}>
                                    <FontAwesomeIcon
                                        className="mr-2"
                                        icon={faTrashAlt}
                                    />
                                    Remove
                                </SmallButton>
                            ) : (
                                ''
                            )}
                        </div>
                    </header>
                    <ValidationMessageDisplay
                        onClear={this.onClearErrorNotificationClicked}
                        errors={errors}
                        message={validationMessage}
                    />
                    <fieldset className="flex-fill d-flex flex-column overflow-y-auto p-3">
                        <NumericInput
                            label="Pay Rate"
                            id="payRate"
                            name="payRate"
                            value={payRate.payRate ?? ''}
                            onChange={onChange}
                            placeholder="Enter Rate"
                            type="number"
                            step="any"
                            error="Pay Rate is required."
                            required
                        />
                        <Input
                            label="Start Date"
                            id="startDate"
                            required
                            type="date"
                            name="startDate"
                            value={payRate.startDate ?? ''}
                            onChange={onChange}
                            error="Start Date is required."
                        />
                        <FormGroup>
                            <Input
                                formGroupClassName="mb-0"
                                label="End Date"
                                id="endDate"
                                type="date"
                                name="endDate"
                                value={payRate.endDate ?? ''}
                                onChange={onChange}
                            />
                            <div className="ml-3 mt-1 d-flex flex-row align-items-center">
                                <span className="pr-2">
                                    {getRateStatusMessage(payRate)}
                                </span>
                            </div>
                        </FormGroup>
                        <Input
                            label="Received On"
                            id="rateReceivedDate"
                            type="date"
                            name="rateReceivedDate"
                            value={
                                payRate.rateReceivedDate ?? ''
                            }
                            onChange={onChange}
                            required
                        />
                    </fieldset>
                </FormValidated>
            </div>
        );
    }
}
